import { useEffect, useRef } from "react";

import { apiFetch } from "./api";

export const FRONT_PERF_AUDIT_ENABLED = parseBooleanFlag(
  import.meta.env.VITE_PERF_AUDIT_ENABLED ?? import.meta.env.PERF_AUDIT_ENABLED,
);

export type FrontPerfTrace = {
  id: string;
  page: string;
  route: string;
  startedAt: number;
  startedAtIso: string;
  marks: Set<string>;
};

type PerfFetchMeta = {
  label: string;
  trace?: FrontPerfTrace | null;
  queryKey?: unknown;
  page?: string;
};

export class PerfHttpError extends Error {
  status: number;
  payload: unknown;
  responseText: string;

  constructor(status: number, payload: unknown, responseText: string) {
    super(`Request failed: ${status}`);
    this.name = "PerfHttpError";
    this.status = status;
    this.payload = payload;
    this.responseText = responseText;
  }
}

export function usePagePerfTrace(page: string) {
  const traceRef = useRef<FrontPerfTrace | null>(null);

  if (!traceRef.current) {
    traceRef.current = {
      id: buildFrontTraceId(),
      page,
      route: window.location.pathname,
      startedAt: performance.now(),
      startedAtIso: new Date().toISOString(),
      marks: new Set<string>(),
    };
  }

  useEffect(() => {
    const trace = traceRef.current;
    if (!trace || !FRONT_PERF_AUDIT_ENABLED) return;

    logFrontEvent("PAGE_START", {
      traceId: trace.id,
      page: trace.page,
      route: trace.route,
      startedAt: trace.startedAtIso,
    });

    return () => {
      logFrontEvent("PAGE_END", {
        traceId: trace.id,
        page: trace.page,
        route: trace.route,
        durationMs: roundDuration(performance.now() - trace.startedAt),
      });
    };
  }, []);

  return traceRef.current;
}

export function useObserveFrontQuery(
  trace: FrontPerfTrace | null | undefined,
  args: {
    label: string;
    queryKey: unknown;
    enabled: boolean;
  },
) {
  const serializedQueryKey = serializeQueryKey(args.queryKey);

  useEffect(() => {
    if (!FRONT_PERF_AUDIT_ENABLED) return;

    logFrontEvent("QUERY_OBSERVED", {
      traceId: trace?.id ?? undefined,
      page: trace?.page ?? undefined,
      label: args.label,
      queryKey: serializedQueryKey,
      enabled: args.enabled,
      sincePageStartMs: trace ? roundDuration(performance.now() - trace.startedAt) : undefined,
    });
  }, [args.enabled, args.label, serializedQueryKey, trace]);
}

export function markFrontMilestone(
  trace: FrontPerfTrace | null | undefined,
  mark: string,
  attrs?: Record<string, unknown>,
) {
  if (!FRONT_PERF_AUDIT_ENABLED || !trace) return;
  if (trace.marks.has(mark)) return;

  trace.marks.add(mark);
  logFrontEvent("PAGE_MARK", {
    traceId: trace.id,
    page: trace.page,
    route: trace.route,
    mark,
    sincePageStartMs: roundDuration(performance.now() - trace.startedAt),
    ...attrs,
  });
}

export async function fetchJsonWithPerf<T>(
  url: string,
  init: RequestInit | undefined,
  meta: PerfFetchMeta,
): Promise<{ data: T; response: Response; serverTraceId: string | null }> {
  const trace = meta.trace ?? null;
  const label = meta.label;
  const queryKey = serializeQueryKey(meta.queryKey);
  const page = meta.page ?? trace?.page;
  const startedAt = performance.now();
  const headers = new Headers(init?.headers ?? {});

  if (FRONT_PERF_AUDIT_ENABLED) {
    if (trace?.id) {
      headers.set("x-perf-client-trace-id", trace.id);
    }
    if (page) {
      headers.set("x-perf-client-page", page);
    }
  }

  if (FRONT_PERF_AUDIT_ENABLED) {
    logFrontEvent("FETCH_START", {
      traceId: trace?.id ?? undefined,
      page,
      label,
      url,
      queryKey,
      sincePageStartMs: trace ? roundDuration(startedAt - trace.startedAt) : undefined,
    });
  }

  const response = await apiFetch(url, {
    ...init,
    headers,
  });
  const headersAt = performance.now();
  const rawText = await response.text();
  const bodyAt = performance.now();
  const data = parseJsonBody<T>(rawText);
  const parsedAt = performance.now();
  const serverTraceId = response.headers.get("x-perf-trace-id");
  const bytes = rawText ? new TextEncoder().encode(rawText).byteLength : 0;

  if (FRONT_PERF_AUDIT_ENABLED) {
    logFrontEvent("FETCH_END", {
      traceId: trace?.id ?? undefined,
      page,
      label,
      url,
      queryKey,
      status: response.status,
      ok: response.ok,
      durationMs: roundDuration(performance.now() - startedAt),
      fetchMs: roundDuration(headersAt - startedAt),
      bodyMs: roundDuration(bodyAt - headersAt),
      parseMs: roundDuration(parsedAt - bodyAt),
      bytes,
      serverTraceId: serverTraceId ?? undefined,
      sincePageStartMs: trace ? roundDuration(performance.now() - trace.startedAt) : undefined,
    });
  }

  if (!response.ok) {
    throw new PerfHttpError(response.status, data, rawText);
  }

  return { data, response, serverTraceId };
}

export function logFrontEvent(kind: string, payload: Record<string, unknown>) {
  if (!FRONT_PERF_AUDIT_ENABLED) return;

  console.info(
    `[PERF][FRONT] ${JSON.stringify({
      ts: new Date().toISOString(),
      kind,
      ...sanitizePayload(payload),
    })}`,
  );
}

export function serializeQueryKey(value: unknown) {
  if (value === undefined) return undefined;

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function sanitizePayload(payload: Record<string, unknown>) {
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined) continue;
    sanitized[key] = value;
  }
  return sanitized;
}

function buildFrontTraceId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}

function parseJsonBody<T>(rawText: string) {
  if (!rawText) {
    return null as T;
  }

  return JSON.parse(rawText) as T;
}

function parseBooleanFlag(value: string | undefined) {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return ["1", "true", "yes", "on"].includes(normalized);
}

function roundDuration(value: number) {
  return Number(value.toFixed(2));
}
